import { action, makeObservable, observable, runInAction } from 'mobx';
import type { ParticipantBdmStateResDto, RetrievalResDto } from '@web/common';
import { RetrievalStatus } from '@prisma/client';
import {
  getBdmStateForUser,
  getRetrievalsForUser,
  setRetrievalStatus,
  switchBdm1SampleToVoucher,
} from '../request/authenticated-requests/user';
import { showErrorNotification } from '../component/notification';

export class RetrievalStore {
  @observable public retrievals: RetrievalResDto[] = [];
  @observable public bdmState: ParticipantBdmStateResDto | null = null;
  @observable public isLoading = false;
  @observable public loadFailed = false;

  constructor() {
    makeObservable(this);
  }

  @action public load = (userId: string) => {
    this.isLoading = true;
    this.loadFailed = false;
    Promise.all([getRetrievalsForUser(userId), getBdmStateForUser(userId)])
      .then(([retrievals, bdmState]) => {
        runInAction(() => {
          this.retrievals = retrievals;
          this.bdmState = bdmState;
          this.isLoading = false;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.isLoading = false;
          this.loadFailed = true;
        });
      });
  };

  @action public updateStatus = (
    userId: string,
    retrievalId: number,
    status: RetrievalStatus,
  ) => {
    setRetrievalStatus(userId, retrievalId, status)
      .then((updated) => {
        runInAction(() => {
          this.retrievals = this.retrievals.map((r) =>
            r.id === updated.id ? updated : r,
          );
        });
      })
      .catch((error) => {
        showErrorNotification(error?.errorCode);
        this.load(userId);
      });
  };

  // Touches bdmState.bdm1Outcome as well as the retrieval, so a full reload (rather than a
  // local merge like updateStatus) keeps both in sync with the server.
  @action public switchBdm1SampleToVoucher = (userId: string) => {
    switchBdm1SampleToVoucher(userId)
      .then(() => this.load(userId))
      .catch((error) => {
        showErrorNotification(error?.errorCode);
        this.load(userId);
      });
  };
}
